import { useQueryClient } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentDeleteMutation } from "web_myplann/Api/Document/useDocumentDeleteMutation";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import type { DocumentDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DeleteDocumentDrawer } from "web_myplann/Portfolio/Components/Redesign/Documents/DeleteDocumentDrawer";
import { MoveToFolderDrawer } from "web_myplann/Portfolio/Components/Redesign/Documents/MoveToFolderDrawer";
import { RenameDocumentForm } from "web_myplann/Portfolio/Forms/RenameDocumentForm/RenameDocumentForm";
import {
	type DocumentActionDrawer,
	useDocumentActions,
} from "web_myplann/Portfolio/Hooks/useDocumentActions";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { ActionsDrawerRedesign } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRedesign";

const R = Resources.Portfolio.List.DocumentActionsDrawer;

type Props = {
	item: DocumentDTO;
	open: boolean;
	onClose: () => void;
	activeTag?: string;
};

export const DocumentActionsDrawerRedesign: FC<Props> = ({
	item,
	open,
	onClose,
	activeTag,
}) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	const { mutate: renameDocument } = useDocumentEditMutation();
	const { mutate: deleteDocument } = useDocumentDeleteMutation();

	const [openDrawer, setOpenDrawer] = useState<DocumentActionDrawer | null>(
		null,
	);

	const actions = useDocumentActions({
		item,
		onClose,
		onOpenDrawer: setOpenDrawer,
	});

	const closeDrawer = () => {
		setOpenDrawer(null);
		onClose();
	};

	const handleRename = (title: string) => {
		renameDocument({
			requestContract: {
				title,
			},
			id: item.id,
		});
		closeDrawer();
		if (activeTag) {
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.list(activeTag),
			});
		}
	};

	return (
		<>
			<ActionsDrawerRedesign
				open={open}
				onClose={onClose}
				title={t(R.title)}
				concealed={openDrawer !== null}
				actions={actions}
				grabber
			/>

			<MyPlannDrawer
				open={openDrawer === "rename"}
				onClose={closeDrawer}
				title={t(R.Rename.documentTitle)}
				grabber
			>
				<RenameDocumentForm title={item.title} onSubmit={handleRename} />
			</MyPlannDrawer>

			<MoveToFolderDrawer
				open={openDrawer === "move"}
				onClose={closeDrawer}
				item={item}
				activeTag={activeTag}
			/>

			<DeleteDocumentDrawer
				open={openDrawer === "delete"}
				documentTitle={item.title}
				onClose={closeDrawer}
				onConfirm={() => {
					closeDrawer();
					deleteDocument(item.id);
				}}
			/>
		</>
	);
};
